import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Share2, Loader2, Check, X, ExternalLink, Music, Youtube, Facebook,
  Instagram, MessageSquareText, AlertTriangle, Globe, FileText,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@/lib/api";

// Static per-platform copy — sourced from lucide-react + shared brand voice.
// Kept in-file (10 lines) to avoid an extra module for a single-view UI.
const PLATFORM_META = {
  tiktok:    { label: "TikTok",    Icon: Music,             blurb: "Uploaded via PULL_FROM_URL" },
  youtube:   { label: "YouTube",   Icon: Youtube,           blurb: "Published as a Short / video" },
  facebook:  { label: "Facebook",  Icon: Facebook,          blurb: "Published to your Page video feed" },
  instagram: { label: "Instagram", Icon: Instagram,         blurb: "Published as a Reel" },
  threads:   { label: "Threads",   Icon: MessageSquareText, blurb: "Published as a video post" },
  x:         { label: "X (Twitter)", Icon: X,               blurb: "Text tweet + public video link (native video upload coming soon)" },
};

/**
 * Slide-over modal for cross-posting a fused TV commercial to all
 * connected socials + optional WordPress publish. Keeps the flow
 * inside the Commercials page so producers don't lose context.
 *
 * Props:
 *   open          — bool, controls visibility
 *   onClose       — () => void
 *   commercial    — full commercial doc (needs commercial_id, advertiser, chosen_script, duration_sec)
 *   chosenScriptText — the currently-chosen script text used to pre-fill caption / WP body
 */
export default function CommercialPublishModal({
  open, onClose, commercial, chosenScriptText,
}) {
  const [targets, setTargets] = useState(null);   // null = loading
  const [selected, setSelected] = useState(() => new Set());
  const [publishWP, setPublishWP] = useState(false);
  const [wpStatus, setWpStatus] = useState("draft");
  const [wpTitle, setWpTitle] = useState("");
  const [caption, setCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);   // per-platform status rows

  // Pre-fill caption + WP title once the modal opens.
  useEffect(() => {
    if (!open) return;
    const adv = commercial?.advertiser || "New spot";
    const dur = commercial?.duration_sec || 30;
    const firstLine = (chosenScriptText || "").split("\n").find((l) => l.trim()) || "";
    setCaption(`${adv} — ${firstLine || `fresh :${dur}s spot now on air`} #radio #commercial`.slice(0, 280));
    setWpTitle(`${adv} — new :${dur}s radio commercial`);
    setResults(null);
  }, [open, commercial, chosenScriptText]);

  // Load which platforms + WP the station has connected.
  const loadTargets = useCallback(async () => {
    if (!commercial?.commercial_id) return;
    setTargets(null);
    try {
      const r = await api.get(`/commercials/${commercial.commercial_id}/publish-targets`);
      setTargets(r.data);
      // Pre-select all connected platforms so a single click can fire the batch.
      const nextSel = new Set((r.data.socials || []).map((s) => s.platform));
      setSelected(nextSel);
      setPublishWP(!!r.data.wordpress?.connected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load your social connections");
      setTargets({ socials: [], wordpress: { connected: false } });
    }
  }, [commercial?.commercial_id]);

  useEffect(() => {
    if (open) loadTargets();
  }, [open, loadTargets]);

  const togglePlatform = (p) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(p)) next.delete(p);
      else next.add(p);
      return next;
    });
  };

  const publish = async () => {
    if (!commercial?.commercial_id) return;
    if (selected.size === 0 && !publishWP) {
      toast.error("Pick at least one destination");
      return;
    }
    setPublishing(true);
    setResults(null);
    try {
      const { data } = await api.post(
        `/commercials/${commercial.commercial_id}/publish-fused`,
        {
          platforms: Array.from(selected),
          caption: caption.trim(),
          publish_wordpress: publishWP,
          wordpress_title: wpTitle.trim() || null,
          wordpress_status: wpStatus,
        },
      );
      setResults({ social: data.results || [], wp: data.wordpress || null });
      const okCount = (data.results || []).filter((r) => r.ok).length +
        (data.wordpress?.ok ? 1 : 0);
      const failCount = (data.results || []).filter((r) => !r.ok).length +
        (data.wordpress && !data.wordpress.ok ? 1 : 0);
      if (failCount === 0) {
        toast.success(`Live on ${okCount} target${okCount === 1 ? "" : "s"}`);
      } else if (okCount === 0) {
        toast.error("Every destination failed — check the results below");
      } else {
        toast.warning(`Live on ${okCount}, failed on ${failCount} — see below`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed to start");
    } finally {
      setPublishing(false);
    }
  };

  const disconnected = useMemo(() => {
    if (!targets) return [];
    const connectedSet = new Set((targets.socials || []).map((s) => s.platform));
    return Object.keys(PLATFORM_META).filter((p) => !connectedSet.has(p));
  }, [targets]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="commercial-publish-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg border border-line bg-bg-surface shadow-2xl"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-line bg-bg-surface px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-brand-amber/10 p-2">
              <Share2 className="h-4 w-4 text-brand-amber" />
            </div>
            <div>
              <div className="font-display text-sm font-bold text-ink-primary">Publish to socials + blog</div>
              <div className="text-[11px] text-ink-muted">
                {commercial?.advertiser} · :{commercial?.duration_sec || 30}s TV commercial
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-line bg-bg p-1.5 text-ink-muted hover:text-ink-primary"
            data-testid="commercial-publish-close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Loading */}
        {!targets && (
          <div className="flex items-center justify-center gap-2 py-16 text-xs text-ink-muted">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading your connected platforms…
          </div>
        )}

        {targets && (
          <div className="p-5 space-y-5">
            {/* Caption editor */}
            <div>
              <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Caption</label>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value.slice(0, 500))}
                rows={3}
                className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-3 py-2 text-sm text-ink-primary resize-none"
                placeholder="Grab attention in the first line — this becomes the tweet / TikTok / Reel caption."
                data-testid="commercial-publish-caption"
              />
              <div className="mt-1 flex items-center justify-between text-[10px] text-ink-muted">
                <span>Applies to all social posts</span>
                <span>{caption.length}/500 · X truncates to 280</span>
              </div>
            </div>

            {/* Social platform selection */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">
                Social platforms {targets.socials?.length ? `(${targets.socials.length} connected)` : ""}
              </div>
              {(!targets.socials || targets.socials.length === 0) && (
                <div className="rounded-md border border-dashed border-line bg-bg-elevated p-4 text-xs text-ink-muted flex items-start gap-2">
                  <AlertTriangle className="h-3 w-3 text-brand-amber shrink-0 mt-0.5" />
                  <div>
                    No socials connected yet. Head to{" "}
                    <a href="/dashboard/marketing" className="text-brand-amber underline">Marketing Kit → Social Connections</a>
                    {" "}to link TikTok, Meta, YouTube, or Threads.
                  </div>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="commercial-publish-platforms">
                {(targets.socials || []).map((s) => {
                  const meta = PLATFORM_META[s.platform] || { label: s.platform, Icon: Share2, blurb: "" };
                  const Icon = meta.Icon;
                  const isSelected = selected.has(s.platform);
                  return (
                    <button
                      key={s.platform}
                      type="button"
                      onClick={() => togglePlatform(s.platform)}
                      data-testid={`commercial-publish-platform-${s.platform}`}
                      className={`text-left rounded-md border p-3 transition ${
                        isSelected
                          ? "border-brand-amber bg-brand-amber/10"
                          : "border-line bg-bg-elevated hover:border-brand-amber/40"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`rounded-md p-1.5 ${isSelected ? "bg-brand-amber/20" : "bg-bg"}`}>
                          <Icon className="h-4 w-4 text-brand-amber" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-semibold text-ink-primary flex items-center gap-2">
                            {meta.label}
                            {isSelected && <Check className="h-3 w-3 text-brand-amber" />}
                          </div>
                          <div className="text-[10px] text-ink-muted truncate">{s.label}</div>
                        </div>
                      </div>
                      <div className="text-[10px] text-ink-muted mt-1.5 leading-relaxed">
                        {meta.blurb}
                      </div>
                    </button>
                  );
                })}
              </div>
              {disconnected.length > 0 && (
                <div className="mt-2 text-[10px] text-ink-muted">
                  Not yet connected: {disconnected.map((p) => PLATFORM_META[p].label).join(" · ")}.{" "}
                  <a href="/dashboard/marketing" className="text-brand-amber underline">Add more</a>
                </div>
              )}
            </div>

            {/* WordPress block */}
            <div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted mb-2">
                WordPress blog
              </div>
              {targets.wordpress?.connected ? (
                <div className={`rounded-md border p-3 transition ${
                  publishWP ? "border-brand-amber bg-brand-amber/10" : "border-line bg-bg-elevated"
                }`}>
                  <label className="flex items-center gap-3 cursor-pointer" data-testid="commercial-publish-wp-toggle">
                    <input
                      type="checkbox"
                      checked={publishWP}
                      onChange={(e) => setPublishWP(e.target.checked)}
                      className="h-4 w-4 accent-brand-amber"
                    />
                    <div className="rounded-md bg-brand-amber/20 p-1.5">
                      <Globe className="h-4 w-4 text-brand-amber" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-semibold text-ink-primary">Publish to WordPress blog</div>
                      <div className="text-[10px] text-ink-muted truncate">{targets.wordpress.site_url || "Connected site"}</div>
                    </div>
                  </label>
                  {publishWP && (
                    <div className="mt-3 space-y-2 pl-11">
                      <div>
                        <label className="text-[10px] font-mono uppercase tracking-wider text-ink-muted flex items-center gap-1">
                          <FileText className="h-3 w-3" /> Post title
                        </label>
                        <input
                          type="text"
                          value={wpTitle}
                          onChange={(e) => setWpTitle(e.target.value.slice(0, 200))}
                          className="mt-1 w-full rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs text-ink-primary"
                          data-testid="commercial-publish-wp-title"
                        />
                      </div>
                      <div className="flex items-center gap-2 text-[11px]">
                        <span className="text-ink-muted">Publish as:</span>
                        {[
                          { id: "draft", label: "Draft" },
                          { id: "publish", label: "Publish live" },
                        ].map((opt) => (
                          <button
                            key={opt.id}
                            type="button"
                            onClick={() => setWpStatus(opt.id)}
                            className={`px-2 py-1 rounded border transition ${
                              wpStatus === opt.id
                                ? "border-brand-amber bg-brand-amber/10 text-brand-amber"
                                : "border-line bg-bg text-ink-muted hover:text-ink-primary"
                            }`}
                            data-testid={`commercial-publish-wp-status-${opt.id}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <div className="text-[10px] text-ink-muted">
                        Post will embed the fused MP4 + include the full spoken script.
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-md border border-dashed border-line bg-bg-elevated p-3 text-xs text-ink-muted flex items-center gap-2">
                  <Globe className="h-3 w-3 shrink-0" />
                  <span>
                    No WordPress site connected —{" "}
                    <a href="/dashboard/blog-connector" className="text-brand-amber underline">connect one</a>
                    {" "}to auto-publish blog posts.
                  </span>
                </div>
              )}
            </div>

            {/* Publish results */}
            {results && (
              <div className="rounded-md border border-line bg-bg-elevated p-3 space-y-2" data-testid="commercial-publish-results">
                <div className="text-[10px] font-mono uppercase tracking-wider text-ink-muted">Results</div>
                {(results.social || []).map((r) => (
                  <ResultRow key={r.platform} row={r} />
                ))}
                {results.wp && (
                  <ResultRow
                    row={{
                      platform: "wordpress",
                      ok: results.wp.ok,
                      error: results.wp.error,
                      external_url: results.wp.url,
                      external_id: results.wp.post_id,
                    }}
                  />
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="sticky bottom-0 z-10 flex items-center justify-between gap-3 border-t border-line bg-bg-surface px-5 py-3">
          <div className="text-[10px] text-ink-muted">
            {selected.size} social{selected.size === 1 ? "" : "s"} · {publishWP ? "WordPress on" : "WordPress off"}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-line bg-bg px-3 py-1.5 text-xs text-ink-secondary hover:text-ink-primary"
            >
              Close
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={publishing || (selected.size === 0 && !publishWP)}
              data-testid="commercial-publish-submit"
              className="inline-flex items-center gap-2 rounded-md bg-brand-amber px-4 py-1.5 text-xs font-semibold text-black amber-glow disabled:opacity-50"
            >
              {publishing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}
              Publish now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultRow({ row }) {
  const meta = PLATFORM_META[row.platform] || { label: row.platform, Icon: Globe };
  const Icon = row.platform === "wordpress" ? Globe : meta.Icon;
  return (
    <div className="flex items-start gap-2 text-[11px]" data-testid={`commercial-publish-result-${row.platform}`}>
      <div className={`rounded-md p-1 shrink-0 ${row.ok ? "bg-emerald-500/10" : "bg-brand-onair/10"}`}>
        {row.ok
          ? <Check className="h-3 w-3 text-emerald-400" />
          : <X className="h-3 w-3 text-brand-onair" />}
      </div>
      <Icon className={`h-3 w-3 mt-0.5 shrink-0 ${row.ok ? "text-ink-secondary" : "text-ink-muted"}`} />
      <div className="flex-1 min-w-0">
        <div className="font-medium text-ink-primary">
          {row.platform === "wordpress" ? "WordPress" : (meta.label || row.platform)}
        </div>
        {row.ok ? (
          row.external_url ? (
            <a
              href={row.external_url}
              target="_blank"
              rel="noreferrer"
              className="text-brand-amber hover:underline inline-flex items-center gap-1"
            >
              View post <ExternalLink className="h-2.5 w-2.5" />
            </a>
          ) : (
            <span className="text-emerald-300">Live — id {row.external_id || "—"}</span>
          )
        ) : (
          <span className="text-brand-onair break-words">{row.error || "Failed"}</span>
        )}
      </div>
    </div>
  );
}
